import { RadioPills } from '@app/components/various/Fields/RadioPills';
import { getIsUpdatingShipping, updateShippingRequest } from '@app/ducks';
import { ORDER_SHIPPING_UPDATE_MUTATION_KEY, useOrderShippingUpdate } from '@app/services/hooks/orders/useOrderShippingUpdate';
import { useDebouncedCallback } from '@app/utils/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Order } from '@typings';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface Props {
  orderId: string;
  splitId?: string;
  methods: Order.ShippingMethod[];
  selectedMethods: Order.SelectedShippingMethod[];
}

const DEBOUNCE_DELAY = 500;

export const CheckoutShippingMethods = ({ orderId, splitId = orderId, methods, selectedMethods }: Props) => {
  const dispatch = useDispatch();
  const isUpdatingShipping = useSelector(getIsUpdatingShipping);

  const queryClient = useQueryClient();

  const getSelectedMethodId = React.useCallback(() => {
    return selectedMethods.find(method => method.splitId === splitId)?.shippingMethodId;
  }, [selectedMethods, splitId]);

  const [selectedId, setSelectedId] = React.useState(getSelectedMethodId);

  React.useEffect(() => {
    const isMutating = queryClient.isMutating({ mutationKey: [ORDER_SHIPPING_UPDATE_MUTATION_KEY] }) > 0;

    if (!isUpdatingShipping && !isMutating) {
      setSelectedId(getSelectedMethodId());
    }
  }, [getSelectedMethodId, isUpdatingShipping, queryClient]);

  const { mutate } = useOrderShippingUpdate();

  const updateOrderShipping = useDebouncedCallback((methodId: string) => {
    mutate({ orderId, shippingMethodId: methodId, splitId });
  }, DEBOUNCE_DELAY);

  const handleChange = (methodId: string) => {
    setSelectedId(methodId);
    updateOrderShipping(methodId);
    dispatch(updateShippingRequest({ orderId: splitId }));
  };

  const options = methods.map(method => ({
    name: method.name,
    postfix: method.totals.price,
    value: method.id,
  }));

  return <RadioPills name={`shippingMethod-${splitId}`} options={options} value={selectedId} color="gray" onChange={handleChange} />;
};
